/** @module @liquidefy/code-editor/keybindings/keybindings @version 3.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  KeybindingsState,
  KeyBinding,
  KeybindingId,
  CommandId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asKeybindingId,
} from '../code-editor.js';

// ═══ §2 STATE FACTORY ═══

/**
 * Returns an empty initial KeybindingsState.
 */
export function createKeybindingsState(): KeybindingsState {
  return {
    bindings: [],
    pendingChordKey: undefined,
    pendingChordId: undefined,
  };
}

// ═══ §3 REGISTRY OPERATIONS ═══

/**
 * Adds a binding to the registry. Rejects if a binding with the same id already exists.
 */
export function registerBinding(
  state: KeybindingsState,
  binding: KeyBinding,
): EditorResult<KeybindingsState> {
  const existing = state.bindings.find((b) => b.id === binding.id);
  if (existing) {
    return err('BINDING_ALREADY_EXISTS', `A binding with id '${binding.id}' is already registered`);
  }
  return ok(
    { ...state, bindings: [...state.bindings, binding] },
    'BINDING_REGISTERED',
    `Registered binding '${binding.id}'`,
  );
}

/**
 * Removes a binding by id.
 */
export function unregisterBinding(
  state: KeybindingsState,
  id: KeybindingId,
): EditorResult<KeybindingsState> {
  const idx = state.bindings.findIndex((b) => b.id === id);
  if (idx === -1) {
    return err('BINDING_NOT_FOUND', `No binding with id '${id}'`);
  }
  const bindings = [
    ...state.bindings.slice(0, idx),
    ...state.bindings.slice(idx + 1),
  ];
  return ok(
    { ...state, bindings },
    'BINDING_UNREGISTERED',
    `Unregistered binding '${id}'`,
  );
}

/**
 * Finds the highest-weight binding matching the given key in the current context.
 * Returns undefined if no binding matches.
 */
export function resolveKey(
  state: KeybindingsState,
  key: string,
  context: Record<string, unknown>,
): KeyBinding | undefined {
  const normalized = normalizeKey(key);
  const candidates = state.bindings.filter((b) => {
    if (normalizeKey(b.key) !== normalized) return false;
    if (b.isChord) return false;
    if (b.when === undefined || b.when === '') return true;
    return evaluateWhenSimple(b.when, context);
  });
  if (candidates.length === 0) return undefined;
  // Higher weight wins; ties broken by position (later wins, like VS Code)
  return candidates.reduce((best, cur) => (cur.weight >= best.weight ? cur : best));
}

/**
 * Returns all bindings registered for the given command.
 */
export function getBindingsForCommand(
  state: KeybindingsState,
  commandId: CommandId,
): ReadonlyArray<KeyBinding> {
  return state.bindings.filter((b) => b.command === commandId);
}

/**
 * Returns all bindings whose key (normalized) matches the given key string.
 */
export function getBindingsByKey(
  state: KeybindingsState,
  key: string,
): ReadonlyArray<KeyBinding> {
  const normalized = normalizeKey(key);
  return state.bindings.filter((b) => normalizeKey(b.key) === normalized);
}

/**
 * Overrides the key of an existing binding, producing a new binding record.
 */
export function overrideBinding(
  state: KeybindingsState,
  id: KeybindingId,
  newKey: string,
): EditorResult<KeybindingsState> {
  if (!isValidKey(newKey)) {
    return err('INVALID_KEY', `Key '${newKey}' is not a valid key expression`);
  }
  const idx = state.bindings.findIndex((b) => b.id === id);
  if (idx === -1) {
    return err('BINDING_NOT_FOUND', `No binding with id '${id}'`);
  }
  const original = state.bindings[idx]!;
  const updated: KeyBinding = { ...original, key: normalizeKey(newKey) };
  const bindings: ReadonlyArray<KeyBinding> = [
    ...state.bindings.slice(0, idx),
    updated,
    ...state.bindings.slice(idx + 1),
  ];
  return ok(
    { ...state, bindings },
    'BINDING_OVERRIDDEN',
    `Overrode key for binding '${id}' to '${newKey}'`,
  );
}

/**
 * Removes all bindings from the state.
 */
export function clearBindings(state: KeybindingsState): KeybindingsState {
  return { ...state, bindings: [] };
}

// ═══ §4 SERIALIZATION ═══

/**
 * Serializes the bindings array to a JSON string.
 */
export function serializeBindings(state: KeybindingsState): string {
  return JSON.stringify(state.bindings);
}

/**
 * Deserializes a JSON string into a KeybindingsState. Validates structure.
 */
export function deserializeBindings(json: string): EditorResult<KeybindingsState> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return err('INVALID_JSON', `Failed to parse bindings JSON: ${String(e)}`);
  }
  if (!Array.isArray(parsed)) {
    return err('INVALID_FORMAT', 'Bindings JSON must be an array');
  }
  const bindings: KeyBinding[] = [];
  for (let i = 0; i < parsed.length; i++) {
    const item = parsed[i] as Record<string, unknown>;
    if (typeof item.id !== 'string' || typeof item.key !== 'string' || typeof item.command !== 'string') {
      return err('INVALID_BINDING', `Binding at index ${i} is missing required fields`);
    }
    bindings.push({
      id: asKeybindingId(item.id),
      key: item.key,
      command: item.command as CommandId,
      when: typeof item.when === 'string' ? item.when : undefined,
      args: item.args,
      isChord: typeof item.isChord === 'boolean' ? item.isChord : false,
      chordFirstPart: typeof item.chordFirstPart === 'string' ? item.chordFirstPart : undefined,
      chordSecondPart: typeof item.chordSecondPart === 'string' ? item.chordSecondPart : undefined,
      source: (item.source === 'user' || item.source === 'extension') ? item.source : 'default',
      weight: typeof item.weight === 'number' ? item.weight : 0,
    });
  }
  return ok(
    { bindings, pendingChordKey: undefined, pendingChordId: undefined },
    'BINDINGS_DESERIALIZED',
    `Deserialized ${bindings.length} bindings`,
  );
}

// ═══ §5 KEY NORMALIZATION & VALIDATION ═══

/**
 * Normalizes a key expression to lowercase canonical form.
 * 'Ctrl+Shift+P' → 'ctrl+shift+p'
 */
export function normalizeKey(key: string): string {
  return key
    .trim()
    .toLowerCase()
    .split('+')
    .map((part) => part.trim())
    .join('+');
}

/**
 * Returns true if the key string is a valid, non-empty key expression.
 */
export function isValidKey(key: string): boolean {
  if (!key || key.trim().length === 0) return false;
  const normalized = normalizeKey(key);
  // Must have at least one non-empty part
  const parts = normalized.split('+').filter((p) => p.length > 0);
  if (parts.length === 0) return false;
  // All parts must be alphanumeric, function keys, or known modifiers
  const validPart = /^(ctrl|alt|shift|meta|cmd|win|f\d{1,2}|[a-z0-9]|backspace|delete|enter|escape|tab|space|home|end|pageup|pagedown|left|right|up|down|insert|semicolon|equal|comma|minus|period|slash|backquote|bracketleft|bracketright|backslash|quote|numpad\d)$/;
  return parts.every((p) => validPart.test(p));
}

// ═══ INTERNAL HELPERS ═══

/**
 * Lightweight when-clause evaluator for resolveKey. Supports &&, !, and simple identifiers.
 * Full when-clause evaluation lives in context.ts.
 */
function evaluateWhenSimple(when: string, context: Record<string, unknown>): boolean {
  const trimmed = when.trim();
  if (trimmed === '') return true;

  // Split on && (lowest precedence)
  if (trimmed.includes('&&')) {
    return trimmed.split('&&').every((part) => evaluateWhenSimple(part.trim(), context));
  }

  // Split on || (next precedence)
  if (trimmed.includes('||')) {
    return trimmed.split('||').some((part) => evaluateWhenSimple(part.trim(), context));
  }

  // Negation
  if (trimmed.startsWith('!')) {
    return !evaluateWhenSimple(trimmed.slice(1).trim(), context);
  }

  // Equality check: key == value
  const eqIdx = trimmed.indexOf('==');
  if (eqIdx !== -1) {
    const lhs = trimmed.slice(0, eqIdx).trim();
    const rhs = trimmed.slice(eqIdx + 2).trim().replace(/^['"]|['"]$/g, '');
    return String(context[lhs] ?? '') === rhs;
  }

  // Simple boolean identifier
  const val = context[trimmed];
  if (val === undefined) return false;
  return Boolean(val);
}
